import { useEffect, useState } from "react";
import React from "react";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import InfoRow from "../../components/InfoRow/InfoRow";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import {
  StudentCardRegenerateExecutePageState,
  StudentCardRejectionReason,
  executeStudentCardRegeneration,
  fetchStudentCardRegenerationPreview,
  getStudentLanguageLabel,
  rejectStudentCardRegeneration,
  translateStudentCardRegenerateExecuteError,
} from "./StudentCardRegenerateExecute.tools";

const StudentCardRegenerateExecute = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<StudentCardRegenerateExecutePageState>({ status: "loading" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectSuccessPopup, setShowRejectSuccessPopup] = useState(false);
  const [selectedRejectReasons, setSelectedRejectReasons] = useState<StudentCardRejectionReason[]>([]);
  const [otherRejectReason, setOtherRejectReason] = useState("");
  const [rejectFormError, setRejectFormError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      const error = translateStudentCardRegenerateExecuteError(language, "Missing regeneration token.");
      setPageState({
        status: "error",
        title: error.title,
        message: error.message,
      });
      return;
    }

    setPageState({ status: "loading" });
    fetchStudentCardRegenerationPreview(token)
      .then((preview) => {
        setPageState({ status: "ready", preview, token });
      })
      .catch((error: any) => {
        const translatedError = translateStudentCardRegenerateExecuteError(language, error?.data?.error?.message);
        setPageState({
          status: "error",
          title: translatedError.title,
          message: translatedError.message,
        });
      });
  }, [language, searchParams]);

  const handleValidate = () => {
    if (pageState.status !== "ready") {
      return;
    }

    setIsSubmitting(true);
    executeStudentCardRegeneration(pageState.token)
      .then(() => setShowSuccessPopup(true))
      .catch((error: any) => {
        const translatedError = translateStudentCardRegenerateExecuteError(language, error?.data?.error?.message);
        setPageState({
          status: "error",
          title: translatedError.title,
          message: translatedError.message,
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleConfirmReject = () => {
    if (pageState.status !== "ready") {
      return;
    }

    if (selectedRejectReasons.length === 0) {
      setRejectFormError(translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.NO_REASON"));
      return;
    }

    if (selectedRejectReasons.includes("other") && !otherRejectReason.trim()) {
      setRejectFormError(translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.OTHER_REQUIRED"));
      return;
    }

    setIsSubmitting(true);
    rejectStudentCardRegeneration(
      pageState.token,
      selectedRejectReasons,
      selectedRejectReasons.includes("other") ? otherRejectReason.trim() : undefined
    )
      .then(() => {
        setShowRejectModal(false);
        setShowRejectSuccessPopup(true);
      })
      .catch((error: any) => {
        const translatedError = translateStudentCardRegenerateExecuteError(language, error?.data?.error?.message);
        setRejectFormError(translatedError.message);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="p-3 p-md-4" id="student-card-regenerate-execute-page">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-2">{translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.TITLE")}</h2>
          <p className="mb-0 silver-gray-font s-regular">
            {translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.DESCRIPTION")}
          </p>
        </Col>
      </Row>

      {pageState.status === "loading" && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {pageState.status === "error" && (
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            <Alert variant="danger">
              <Alert.Heading>{pageState.title}</Alert.Heading>
              <p className="mb-0">{pageState.message}</p>
            </Alert>
            <DstButton
              variant="primary"
              value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.SUCCESS.POPUP_BUTTON")}
              clickFunction={() => navigate("/")}
            />
          </Col>
        </Row>
      )}

      {pageState.status === "ready" && (
        <Row className="justify-content-center s-regular">
          <Col lg={8} xl={6}>
            <div className="white-background border rounded p-3">
              <h3 className="s-bold mb-3">
                {translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.TITLE")}
              </h3>

              {InfoRow(
                "dst-icon-user-01",
                translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.FULL_NAME"),
                <Link
                  to={`/user/${pageState.preview.user_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-flex align-items-center text-decoration-none"
                >
                  {pageState.preview.full_name}
                  <span className="dst-icon-link-external-01 ms-1" />
                </Link>
              )}
              {InfoRow(
                "dst-icon-mail-02",
                translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.EMAIL"),
                <a
                  href={`mailto:${pageState.preview.email}`}
                  className="d-inline-flex align-items-center text-decoration-none"
                >
                  {pageState.preview.email}
                  <span className="dst-icon-mail-01 ms-1" />
                </a>
              )}
              {InfoRow(
                "dst-icon-calendar-date",
                translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.BIRTHDATE"),
                pageState.preview.birthdate
              )}

              {pageState.preview.image_link ? (
                <React.Fragment>
                  {InfoRow(
                    "dst-icon-image-user",
                    translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.IMAGE_LINK"),
                    <a
                      href={pageState.preview.image_link}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-inline-flex align-items-center text-decoration-none"
                    >
                      {translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.DOWNLOAD_IMAGE_LINK")}
                      <span className="dst-icon-download-01 ms-1" />
                    </a>
                  )}
                  <div className="text-center mt-2 mb-3">
                    <img
                      src={pageState.preview.image_link}
                      alt={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.IMAGE_PREVIEW_ALT")}
                      className="img-fluid rounded border"
                      style={{ width: "300px" }}
                    />
                  </div>
                </React.Fragment>
              ) : null}

              {pageState.preview.requested_at
                ? InfoRow(
                    "dst-icon-clock",
                    translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.RECAP.REQUESTED_AT"),
                    formatDateHumanly(language, pageState.preview.requested_at, true)
                  )
                : null}
            </div>

            <div className="mt-4 d-flex flex-column flex-sm-row gap-2">
              <PermissionComponent
                neededPermission={[{ methods: ["POST"], route: "/student-cards/regenerate/confirm" }]}
              >
                <DstButton
                  variant="primary"
                  value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.VALIDATE_BUTTON")}
                  clickFunction={handleValidate}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </PermissionComponent>
              <PermissionComponent
                neededPermission={[{ methods: ["POST"], route: "/student-cards/regenerate/reject" }]}
              >
                <DstButton
                  variant="outline-danger"
                  value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT_BUTTON")}
                  clickFunction={() => {
                    setSelectedRejectReasons([]);
                    setOtherRejectReason("");
                    setRejectFormError(null);
                    setShowRejectModal(true);
                  }}
                  disabled={isSubmitting}
                />
              </PermissionComponent>
            </div>
          </Col>
        </Row>
      )}

      <Popup
        show={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          navigate("/");
        }}
        modalTitle={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.SUCCESS.POPUP_TITLE")}
        footerContent={
          <DstButton
            variant="primary"
            value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.SUCCESS.POPUP_BUTTON")}
            clickFunction={() => {
              setShowSuccessPopup(false);
              navigate("/");
            }}
          />
        }
      >
        <p className="mb-0">{translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.SUCCESS.POPUP_MESSAGE")}</p>
      </Popup>

      <Popup
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        modalTitle={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.MODAL_TITLE")}
        footerContent={
          <React.Fragment>
            <DstButton
              variant="outline-secondary"
              value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.CANCEL_BUTTON")}
              clickFunction={() => setShowRejectModal(false)}
              disabled={isSubmitting}
            />
            <DstButton
              variant="danger"
              value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.CONFIRM_BUTTON")}
              clickFunction={handleConfirmReject}
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </React.Fragment>
        }
      >
        <p className="mb-3">{translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.MODAL_DESCRIPTION")}</p>
        <Form>
          <Form.Check
            type="checkbox"
            id="reject-invalid-image"
            className="mb-2"
            label={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.REASON_INVALID_IMAGE")}
            checked={selectedRejectReasons.includes("invalid_image")}
            onChange={(event) => {
              setSelectedRejectReasons((current) =>
                event.target.checked
                  ? [...current, "invalid_image"]
                  : current.filter((reason) => reason !== "invalid_image")
              );
              setRejectFormError(null);
            }}
          />
          <Form.Check
            type="checkbox"
            id="reject-invalid-birthdate"
            className="mb-2"
            label={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.REASON_INVALID_BIRTHDATE")}
            checked={selectedRejectReasons.includes("invalid_birthdate")}
            onChange={(event) => {
              setSelectedRejectReasons((current) =>
                event.target.checked
                  ? [...current, "invalid_birthdate"]
                  : current.filter((reason) => reason !== "invalid_birthdate")
              );
              setRejectFormError(null);
            }}
          />
          <Form.Check
            type="checkbox"
            id="reject-other"
            className="mb-2"
            label={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.REASON_OTHER")}
            checked={selectedRejectReasons.includes("other")}
            onChange={(event) => {
              setSelectedRejectReasons((current) =>
                event.target.checked ? [...current, "other"] : current.filter((reason) => reason !== "other")
              );
              setRejectFormError(null);
            }}
          />
          {selectedRejectReasons.includes("other") && pageState.status === "ready" ? (
            <React.Fragment>
              <p className="mb-2 silver-gray-font s-regular">
                {translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.OTHER_LANGUAGE_HINT").replace(
                  "{{language}}",
                  getStudentLanguageLabel(language, pageState.preview.language)
                )}
              </p>
              <Form.Control
                as="textarea"
                rows={3}
                className="mt-2"
                value={otherRejectReason}
                placeholder={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.OTHER_PLACEHOLDER")}
                onChange={(event) => {
                  setOtherRejectReason(event.target.value);
                  setRejectFormError(null);
                }}
              />
            </React.Fragment>
          ) : null}
          {rejectFormError ? (
            <Alert variant="danger" className="mt-3 mb-0">
              {rejectFormError}
            </Alert>
          ) : null}
        </Form>
      </Popup>

      <Popup
        show={showRejectSuccessPopup}
        onClose={() => {
          setShowRejectSuccessPopup(false);
          navigate("/");
        }}
        modalTitle={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.SUCCESS_POPUP_TITLE")}
        footerContent={
          <DstButton
            variant="primary"
            value={translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.SUCCESS.POPUP_BUTTON")}
            clickFunction={() => {
              setShowRejectSuccessPopup(false);
              navigate("/");
            }}
          />
        }
      >
        <p className="mb-0">
          {translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.SUCCESS_POPUP_MESSAGE")}
        </p>
      </Popup>
    </div>
  );
};

export default StudentCardRegenerateExecute;
